import * as React from "react";
import Box from "@mui/material/Box";
import MuiStepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {
  axiosInstance,
  buildLegacyHeaders,
  normalizeCropYearShape,
  normalizeFourRAssessmentShape,
} from "./axiosFetchers";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../Components/Auth/useAuth";
import { buildSparsePayload, toOptionalNumber } from "../utils/sparsePayload";
import { trackEvent } from "../analytics/ga4.js";
import { FOUR_R_ASSESSMENT_CREATOR } from "../Assets/contextFactories_V0";
import { buildFourRAssessmentPayload } from "../utils/assessmentPayload.js";
import { modelInputsOrNull } from "../utils/holosModelInputs";

const MAX_CULTIVATION_OPERATIONS = 4;
const MAX_FERTILIZER_APPLICATIONS = 3;
const MAX_PESTICIDE_APPLICATIONS = 5;

function buildFixedOperationRows(rows = [], rowCount) {
  return Array.from({ length: rowCount }, (_, index) => {
    const row = rows[index] || {};

    return {
      machine_id: row?.machine_id || null,
      hours_used: toOptionalNumber(row?.hours_used) ?? null,
      operation_date: row?.operation_date || null,
      fuel_used_litres: toOptionalNumber(row?.fuel_used_litres) ?? null,
    };
  });
}

function buildFixedFertilizerRows(rows = []) {
  return Array.from({ length: MAX_FERTILIZER_APPLICATIONS }, (_, index) => {
    const row = rows[index] || {};
    return {
      machine_id: row?.machine_id || null,
      hours_used: toOptionalNumber(row?.hours_used) ?? null,
      application_date: row?.application_date || null,
      fuel_used_litres: toOptionalNumber(row?.fuel_used_litres) ?? null,
    };
  });
}

function plantingOperationHasData(operation = {}) {
  return Boolean(
    operation.type ||
      operation.machine_id ||
      operation.operation_date ||
      toOptionalNumber(operation.hours_used) != null ||
      toOptionalNumber(operation.fuel_used_litres) != null ||
      operation.applied_fertilizer
  );
}

function buildCropYearPayload(cropYearState) {
  const payload = buildSparsePayload({
    crop_year: toOptionalNumber(cropYearState?.crop_year),
    crop_this_year: cropYearState?.crop_this_year,
    crop_frequency: cropYearState?.crop_frequency,
    yield_value: toOptionalNumber(cropYearState?.yield_value),
    yield_units: cropYearState?.yield_units,
    previous_crop: cropYearState?.previous_crop,
    tillage_regime: cropYearState?.tillage_regime,
    previous_tillage_regime: cropYearState?.previous_tillage_regime,
    regime_change_date: cropYearState?.regime_change_date,
    residue_management: cropYearState?.residue_management,
    holos_model_inputs: modelInputsOrNull(cropYearState?.holos_model_inputs),
    soil_organic_matter_percent: toOptionalNumber(
      cropYearState?.soil_organic_matter_percent
    ),
    soil_organic_matter_date: cropYearState?.soil_organic_matter_date,
    is_irrigated: cropYearState?.is_irrigated,
    irrigation_water_applied: toOptionalNumber(
      cropYearState?.irrigation_water_applied
    ) ?? null,
    irrigation_water_unit: cropYearState?.irrigation_water_unit || null,
    irrigation_energy_source: cropYearState?.irrigation_energy_source || null,
    irrigation_energy_used: toOptionalNumber(
      cropYearState?.irrigation_energy_used
    ) ?? null,
    irrigation_energy_unit: cropYearState?.irrigation_energy_unit || null,
    planting_operation: plantingOperationHasData(
      cropYearState?.planting_operation
    )
      ? {
          type: cropYearState?.planting_operation?.type,
          hours_used: toOptionalNumber(
            cropYearState?.planting_operation?.hours_used
          ),
          machine_id: cropYearState?.planting_operation?.machine_id,
          applied_fertilizer:
            cropYearState?.planting_operation?.applied_fertilizer,
          operation_date: cropYearState?.planting_operation?.operation_date,
          fuel_used_litres: toOptionalNumber(
            cropYearState?.planting_operation?.fuel_used_litres
          ),
        }
      : undefined,
    harvest: {
      swather: {
        machine_id: cropYearState?.harvest?.swather?.machine_id,
        hours_used: toOptionalNumber(cropYearState?.harvest?.swather?.hours_used),
        operation_date: cropYearState?.harvest?.swather?.operation_date,
        fuel_used_litres: toOptionalNumber(
          cropYearState?.harvest?.swather?.fuel_used_litres
        ),
      },
      combine: {
        machine_id: cropYearState?.harvest?.combine?.machine_id,
        hours_used: toOptionalNumber(cropYearState?.harvest?.combine?.hours_used),
        avg_speed: toOptionalNumber(cropYearState?.harvest?.combine?.avg_speed),
        operation_date: cropYearState?.harvest?.combine?.operation_date,
        fuel_used_litres: toOptionalNumber(
          cropYearState?.harvest?.combine?.fuel_used_litres
        ),
      },
      crop_drying_type: cropYearState?.harvest?.crop_drying_type,
      crop_drying_fuel: cropYearState?.harvest?.crop_drying_fuel,
      moisture: {
        before_drying: toOptionalNumber(
          cropYearState?.harvest?.moisture?.before_drying
        ),
        after_drying: toOptionalNumber(
          cropYearState?.harvest?.moisture?.after_drying
        ),
      },
    },
    fertilizer_rates: Object.fromEntries(
      Object.entries(cropYearState?.fertilizer_rates || {}).map(
        ([nutrient, rates]) => [
          nutrient,
          {
            pre_seed: toOptionalNumber(rates?.pre_seed),
            with_seed: toOptionalNumber(rates?.with_seed),
            post_seed: toOptionalNumber(rates?.post_seed),
          },
        ]
      )
    ),
    cultivations: buildFixedOperationRows(
      cropYearState?.cultivations,
      MAX_CULTIVATION_OPERATIONS
    ),
    fertilizer_applications: buildFixedFertilizerRows(
      cropYearState?.fertilizer_applications
    ),
    pesticide_applications: buildFixedOperationRows(
      cropYearState?.pesticide_applications,
      MAX_PESTICIDE_APPLICATIONS
    ),
    workflow_state: cropYearState?.workflow_state,
    data_status: cropYearState?.data_status,
  });
  const operation = (row = {}, includeSpeed = false) => ({ machine_id: row.machine_id || null,
    hours_used: toOptionalNumber(row.hours_used) ?? null,
    operation_date: row.operation_date || null,
    fuel_used_litres: toOptionalNumber(row.fuel_used_litres) ?? null,
    ...(includeSpeed ? { avg_speed: toOptionalNumber(row.avg_speed) ?? null } : {}) });
  payload.harvest = { ...payload.harvest,
    swather: operation(cropYearState?.harvest?.swather),
    combine: operation(cropYearState?.harvest?.combine, true),
    additional_combines: (cropYearState?.harvest?.additional_combines || []).map((row) => operation(row, true)),
  };
  return payload;
}


export default function HorizontalNonLinearStepper({
  steps,
  farm,
  setFarm,
  field,
  cropYear,
  setCropYear,
  fourRAssessment,
  setFourRAssessment,
  fourRState,
  setFourRState,
  hasUnsavedChanges = false,
  onSaveSuccess = () => {},
  commitDraft = null,
  activeStep = 0,
  onStepChange = () => {},
  readOnly = false,
  summaryContent = null,
}) {
  const { user } = useAuth();
  const [completed, setCompleted] = React.useState({});
  const [saveState, setSaveState] = React.useState("idle");
  const [saveError, setSaveError] = React.useState("");
  const navigate = useNavigate();
  const { farmId, fieldId, cropyearId } = useParams();

  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && completedSteps() !== totalSteps()
        ? steps.findIndex((step, index) => !(index in completed))
        : activeStep + 1;

    onStepChange(newActiveStep);
  };

  const handleBack = () => {
    onStepChange(activeStep - 1);
  };

  const handleStep = (step) => () => {
    if (readOnly) {
      return;
    }

    onStepChange(step);
  };

  async function saveAssessment(currentCropYearId) {
    if (!currentCropYearId) {
      return { assessment: null, hasAssessment: false };
    }

    if (!fourRState.has4RAssessment) {
      if (cropYear?.has_assessment) {
        await axiosInstance.delete(
          `${process.env.REACT_APP_API_URL}/assessments/${currentCropYearId}`,
          {
            headers: buildLegacyHeaders(user),
          }
        );
      }
      setFourRAssessment(FOUR_R_ASSESSMENT_CREATOR());
      return { assessment: null, hasAssessment: false };
    }

    const assessmentPayload = buildFourRAssessmentPayload({
      ...fourRAssessment,
      crop_year_id: currentCropYearId,
    });
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_API_URL}/assessments/${currentCropYearId}`,
      assessmentPayload,
      {
        headers: buildLegacyHeaders(user),
      }
    );

    if (response?.data && typeof response.data === "object") {
      const normalizedAssessment = normalizeFourRAssessmentShape(response.data);
      setFourRAssessment(normalizedAssessment);
      setCropYear((prevState) => ({
        ...prevState,
        has_assessment: true,
      }));
      return { assessment: normalizedAssessment, hasAssessment: true };
    }

    return { assessment: null, hasAssessment: true };
  }

  async function saveCropYear() {
    setSaveState("saving");
    setSaveError("");

    try {
      const payload = buildCropYearPayload(cropYear);
      const response = commitDraft
        ? await commitDraft(payload, { has_assessment: Boolean(fourRState.has4RAssessment), assessment: fourRState.has4RAssessment ? buildFourRAssessmentPayload(fourRAssessment) : null })
        : await axiosInstance.patch(
          `${process.env.REACT_APP_API_URL}/farms/${farm.id}/fields/${field.id}/cropyears/${cropyearId}`,
          payload, { headers: buildLegacyHeaders(user) });

      const normalizedCropYear = normalizeCropYearShape(response.data);
      if (response.hasNewerEdits) {
        setSaveState("saved");
        setSaveError("Saved the selected snapshot. Newer edits remain in your recoverable draft.");
        onSaveSuccess({ savedCropYear: normalizedCropYear, keepDraft: true });
        return normalizedCropYear;
      }
      const assessmentResult = commitDraft
        ? { assessment: response.data.assessment ? normalizeFourRAssessmentShape(response.data.assessment) : null, hasAssessment: Boolean(response.data.has_assessment) }
        : await saveAssessment(response.data.id);
      if (commitDraft) setFourRAssessment(assessmentResult.assessment || FOUR_R_ASSESSMENT_CREATOR());
      const finalCropYear = {
        ...normalizedCropYear,
        has_assessment: assessmentResult.hasAssessment,
      };
      setCropYear(finalCropYear);
      onSaveSuccess({
        savedCropYear: finalCropYear,
        savedFourRAssessment: assessmentResult.assessment,
      });
      trackEvent("cropyear_saved", {
        workflow_area: "cropyear",
        is_new_record: false,
        result: "success",
      });
      setSaveState("saved");
      return finalCropYear;
    } catch (error) {
      console.error(error);
      setSaveState("error");
      setSaveError(typeof error.response?.data?.detail === "string" ? error.response.data.detail : error.message || "We couldn't save your crop year changes.");
      throw error;
    }
  }

  async function handleCompleteStep(isInputValid) {
    if (!isInputValid()) {
      return;
    }

    try {
      const savedCropYear = hasUnsavedChanges ? await saveCropYear() : cropYear;
      const newCompleted = { ...completed, [activeStep]: true };
      setCompleted(newCompleted);

      if (isLastStep()) {
        navigate(
          `/farm/${farmId}/field/${fieldId}/cropyear/${savedCropYear.id}/addanalysis`
        );
        return;
      }

      handleNext();
    } catch (error) {
      return;
    }
  }

  async function handleSaveDraft() {
    try {
      const savedCropYear = await saveCropYear();
      navigate(`/farm/${farmId}/field/${fieldId}`, {
        state: { savedCropYearId: savedCropYear.id },
      });
    } catch (error) {
      return;
    }
  }

  const LowerPanelComponent = ({
    activeStep,
    handleBack,
    steps,
    isInputValid,
  }) => {
    if (readOnly) {
      return null;
    }

    const inputIsValid = isInputValid();

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 1,
          pt: 2,
          pb: 3,
          mt: 1,
        }}
      >
        <Button
          variant="contained"
          color="inherit"
          disabled={activeStep === 0 || saveState === "saving"}
          onClick={handleBack}
          sx={{
            borderRadius: "4px",
            px: 2.5,
            textTransform: "none",
          }}
        >
          Back
        </Button>
        <Box sx={{ flex: "1 1 auto", minWidth: 12 }} />
        <Button
          variant="outlined"
          disabled={
            saveState === "saving" ||
            !hasUnsavedChanges ||
            !inputIsValid
          }
          onClick={handleSaveDraft}
          sx={{
            borderRadius: "4px",
            px: 2.5,
            textTransform: "none",
          }}
        >
          Save Draft and Exit
        </Button>
        <Button
          variant="contained"
          disabled={
            saveState === "saving" ||
            !inputIsValid
          }
          onClick={() => handleCompleteStep(isInputValid)}
          sx={{
            borderRadius: "4px",
            px: 2.5,
            textTransform: "none",
          }}
        >
          {activeStep === steps.length - 1 ? "Save and Review" : "Save and Continue"}
        </Button>
      </Box>
    );
  };

  const CurrentForm =
    activeStep < steps.length ? steps[activeStep].formComponent : null;

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <div className="overflow-hidden rounded-[8px] border border-slate-200 bg-[#fcfcfa] shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
        {summaryContent ? (
          <div className="px-4 py-4 md:px-5">{summaryContent}</div>
        ) : null}
        <div
          className={`px-2 pb-3 pt-3 md:px-3 ${
            summaryContent ? "border-t border-slate-200" : ""
          }`}
        >
          <MuiStepper
            sx={{
              overflow: "hidden",
              backgroundColor: "transparent",
              padding: "0",
            }}
            nonLinear
            activeStep={activeStep}
            alternativeLabel
          >
            {steps.map((step, index) => (
              <Step
                key={step.label}
                completed={completed[index]}
                sx={{
                  "& .MuiStepLabel-root .Mui-completed": {
                    color: "rgb(241,93,34)",
                  },
                  "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel":
                    {
                      color: "#5b6472",
                    },
                  "& .MuiStepLabel-root .Mui-active": {
                    color: "rgb(241,93,34)",
                  },
                  "& .MuiStepLabel-label.Mui-active.MuiStepLabel-alternativeLabel":
                    {
                      color: "#1e293b",
                    },
                  "& .MuiStepLabel-root .Mui-active .MuiStepIcon-text": {
                    fill: "white",
                  },
                  "& .MuiStepLabel-label": {
                    color: "#64748b",
                    fontWeight: 600,
                    marginTop: "4px",
                  },
                }}
              >
                <StepButton
                  disabled={readOnly}
                  sx={{ color: "#FF0000", borderRadius: "9999px" }}
                  onClick={handleStep(index)}
                >
                  {step.label}
                </StepButton>
              </Step>
            ))}
          </MuiStepper>
        </div>
      </div>

      {saveState !== "idle" ? (
        <div className="mt-3 rounded-[8px] border border-slate-200 bg-[#fcfcfa] px-4 py-3 text-sm text-[#666666]">
          {saveState === "saving" && <p>Saving your draft changes...</p>}
          {saveState === "saved" && (
            <p>
              Saved. Current status:{" "}
              {cropYear.workflow_state === "READY" ? "Ready" : "Draft"}.
            </p>
          )}
          {saveState === "error" && <p className="text-[#c62828]">{saveError}</p>}
        </div>
      ) : null}

      <div
        className="mt-3 overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_14px_34px_rgba(18,28,45,0.06)]"
        style={{ minHeight: "360px" }}
      >
        {CurrentForm ? (
          <div
            className={`h-full w-full px-4 py-4 md:px-5 ${
              readOnly ? "pointer-events-none opacity-85" : ""
            }`}
          >
            <CurrentForm
              LowerPanel={LowerPanelComponent}
              panelControls={{
                activeStep,
                handleBack,
                handleNext,
                steps,
                completedSteps,
                totalSteps,
              }}
              farm={farm}
              setFarm={setFarm}
              field={field}
              cropYear={cropYear}
              setCropYear={setCropYear}
              fourRAssessment={fourRAssessment}
              setFourRAssessment={setFourRAssessment}
              fourRState={fourRState}
              setFourRState={setFourRState}
            />
          </div>
        ) : (
          <Typography sx={{ mt: 2 }}>No form is available for this step.</Typography>
        )}
      </div>
    </Box>
  );
}

export { buildCropYearPayload, buildFourRAssessmentPayload };
